import { Box, Grid, Stack, Typography } from "@mui/material"
import { FormCard } from "../../../../../../../shared/components/forms/FormCard"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import React, { useState } from "react"
import { makeStyles } from "tss-react/mui"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { ConnectionTestResult, POSApiType, POSResultStatus } from "../../adyenTypes"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { useApiMutation } from "../../../../../../../core/api/useApiMutation"
import { adyenConnectionTest } from "../../adyenApi"
import { DateTime } from "luxon"
import { StandardChip } from "../../../../../../../shared/components/badges/StandardChip"

const useStyles = makeStyles()(() => ({
    runButton: {
        width: "fit-content",
        padding: ".5rem 2rem !important",
    },
    resultRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
}))

export function ConnectionTest() {
    const { classes } = useStyles()

    const dateTimeFactory = useDateTime()
    const [connectionStatus, setConnectionStatus] = useState<Array<ConnectionTestResult> | null>(null)
    const [lastRun, setLastRun] = useState<string>("-")

    const { mutate: callTestConnection, isLoading } = useApiMutation(adyenConnectionTest, {
        async onSuccess(_, data) {
            setConnectionStatus(data.adyenConnectionTest.results)
            setLastRun(dateTimeFactory.formatDateTime(DateTime.now()))
        },
        async onError(_params) {
            setConnectionStatus(null)
            setLastRun(dateTimeFactory.formatDateTime(DateTime.now()))
        },
    })

    const getStatusChip = (api: POSApiType) => {
        const status = connectionStatus?.find((result) => result.api === api)?.result

        if (status === POSResultStatus.OK) {
            return (
                <LocalizedStrict id="support-reports-health-adyen-api-status-ok">
                    <StandardChip variant="success">OK</StandardChip>
                </LocalizedStrict>
            )
        }
        if (status === POSResultStatus.FAILED) {
            return (
                <LocalizedStrict id="support-reports-health-adyen-api-status-failed">
                    <StandardChip variant="red">Failed</StandardChip>
                </LocalizedStrict>
            )
        }
        return (
            <LocalizedStrict id="support-reports-health-adyen-api-status-unknown">
                <StandardChip variant="grey">Unknown</StandardChip>
            </LocalizedStrict>
        )
    }

    return (
        <Grid item xs={8} md={5}>
            <FormCard>
                <Stack direction="column" spacing="2rem">
                    <LocalizedStrict id="support-reports-health-adyen-summary-aside-title">
                        <Typography variant="subtitle1" fontWeight="bold">
                            API connection test
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="support-reports-health-adyen-run-button">
                        <StandardButton
                            className={classes.runButton}
                            disabled={isLoading}
                            onClick={() => callTestConnection({})}
                        >
                            Run
                        </StandardButton>
                    </LocalizedStrict>
                    <Stack direction="column" spacing=".75rem">
                        <LocalizedStrict id="support-reports-health-adyen-results">
                            <Typography variant="subtitle2">Results:</Typography>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-reports-health-adyen-summary-last-run" vars={{ lastRun }}>
                            <Typography variant="body2" color="textSecondary">
                                Last run: {lastRun}
                            </Typography>
                        </LocalizedStrict>
                        <Box className={classes.resultRow}>
                            <LocalizedStrict id="support-reports-health-adyen-summary-legal-entity-management">
                                <Typography variant="body2" color="textSecondary">
                                    Legal Entity Management:
                                </Typography>
                            </LocalizedStrict>
                            {getStatusChip(POSApiType.ADYEN_LEM)}
                        </Box>
                        <Box className={classes.resultRow}>
                            <LocalizedStrict id="support-reports-health-adyen-summary-balance-platform">
                                <Typography variant="body2" color="textSecondary">
                                    Balance Platform:
                                </Typography>
                            </LocalizedStrict>
                            {getStatusChip(POSApiType.ADYEN_BP)}
                        </Box>
                        <Box className={classes.resultRow}>
                            <LocalizedStrict id="support-reports-health-adyen-summary-psp-platform">
                                <Typography variant="body2" color="textSecondary">
                                    PSP Platform:
                                </Typography>
                            </LocalizedStrict>
                            {getStatusChip(POSApiType.ADYEN_PSP)}
                        </Box>
                    </Stack>
                </Stack>
            </FormCard>
        </Grid>
    )
}
